import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/landingpage/Header';
import Footer from '../../components/landingpage/Footer';
import TipTapViewer from '../../components/landingpage/TipTapViewer';

const BLOGS_SEARCH_URL = '/api/globals/collection/blogs/search';

const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1455390582262-044cdead277a?w=800&q=80';

function avatarUrl(name: string) {
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=017e6e&color=fff&size=128`;
}

function estimateReadTime(content: any) {
  const html = typeof content === 'string' ? content : JSON.stringify(content);
  const words = html.replace(/<[^>]*>/g, ' ').split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

interface BlogDetail {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  author: string;
  authorAvatar: string;
  authorBio: string;
  date: string;
  category: string;
  categoryId: string;
  image: string;
  viewCount: number;
  tags: string[];
}

interface RelatedPost {
  id: string;
  slug: string;
  title: string;
  image: string;
  author: string;
  date: string;
  category: string;
}

function mapDetail(item: any): BlogDetail {
  const d = item.data ?? {};
  // navigate resolves 'Blog Type' ID → object
  const typeData = (d['Blog Type'] && typeof d['Blog Type'] === 'object') ? d['Blog Type'].data ?? {} : {};
  const rawDate = d['Publish Date'];
  const publishDate = rawDate
    ? new Date(rawDate).toLocaleDateString('vi-VN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '';
  const content = d.Content ?? '';
  return {
    id: item.id ?? item._id,
    title: d.Title ?? '',
    slug: d.Slug ?? '',
    excerpt: d.Excerpt ?? '',
    content,
    author: d.Author ?? 'Admin',
    authorAvatar: avatarUrl(d.Author ?? 'Admin'),
    authorBio: 'Giảng viên tại AnhNgu Jennifer.',
    date: publishDate,
    category: typeData.name ?? '',
    categoryId: typeof d['Blog Type'] === 'object' ? d['Blog Type']?.id ?? '' : d['Blog Type'] ?? '',
    image: d.Thumbnail || PLACEHOLDER_IMAGE,
    viewCount: 0,
    tags: d.Tags ?? [],
  };
}

function mapRelated(item: any): RelatedPost {
  const d = item.data ?? {};
  const typeData = (d['Blog Type'] && typeof d['Blog Type'] === 'object') ? d['Blog Type'].data ?? {} : {};
  const rawDate = d['Publish Date'];
  const publishDate = rawDate
    ? new Date(rawDate).toLocaleDateString('vi-VN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '';
  return {
    id: item.id ?? item._id,
    slug: d.Slug ?? '',
    title: d.Title ?? '',
    image: d.Thumbnail || PLACEHOLDER_IMAGE,
    author: d.Author ?? 'Admin',
    date: publishDate,
    category: typeData.name ?? '',
  };
}

// ── Icons ──────────────────────────────────────────────────────────────────────

const HeartIcon: React.FC<{ filled?: boolean }> = ({ filled }) => (
  <svg className="w-5 h-5" fill={filled ? '#017e6e' : 'none'} stroke={filled ? '#017e6e' : 'currentColor'} strokeWidth={1.8} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const CommentIcon: React.FC = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const BookmarkIcon: React.FC<{ filled?: boolean }> = ({ filled }) => (
  <svg className="w-5 h-5" fill={filled ? '#017e6e' : 'none'} stroke={filled ? '#017e6e' : 'currentColor'} strokeWidth={1.8} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
  </svg>
);

const ShareIcon: React.FC = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13" />
  </svg>
);

const ArrowLeftIcon: React.FC = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 12H5M12 5l-7 7 7 7" />
  </svg>
);

// ── Main Page ──────────────────────────────────────────────────────────────────

const BlogDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [post, setPost] = useState<BlogDetail | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<RelatedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPost = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(BLOGS_SEARCH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          page: 1,
          limit: 1,
          navigate: ['Blog Type'],
          filterGroup: {
            type: 'and',
            groups: [{ field: 'Slug', op: { $eq: slug } }],
          },
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const items = json?.data?.items ?? [];
      if (items.length === 0) {
        navigate('/blogs', { replace: true });
        return;
      }
      const mapped = mapDetail(items[0]);
      setPost(mapped);

      // Fetch related posts (same blog type, exclude current slug)
      if (mapped.categoryId) {
        const relRes = await fetch(BLOGS_SEARCH_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            page: 1,
            limit: 3,
            sortBy: 'Publish Date',
            sortOrder: 'desc',
            navigate: ['Blog Type'],
            filterGroup: {
              type: 'and',
              groups: [
                { field: 'Blog Type', op: { $eq: mapped.categoryId } },
                { field: 'Slug', op: { $ne: slug } },
                { field: 'Public', op: { $eq: true } },
              ],
            },
          }),
        });
        if (relRes.ok) {
          const relJson = await relRes.json();
          setRelatedPosts((relJson?.data?.items ?? []).map(mapRelated));
        }
      }
    } catch {
      setError('Không thể tải bài viết. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  const handleLike = () => {
    setLiked((prev) => {
      setLikeCount((c) => (prev ? c - 1 : c + 1));
      return !prev;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-16 w-full animate-pulse">
          <div className="h-4 bg-gray-100 rounded w-32 mb-8" />
          <div className="w-full aspect-[21/9] rounded-3xl bg-gray-100 mb-10" />
          <div className="h-8 bg-gray-100 rounded w-3/4 mb-4" />
          <div className="h-4 bg-gray-100 rounded w-full mb-2" />
          <div className="h-4 bg-gray-100 rounded w-5/6" />
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center flex-col gap-4">
          <p className="text-red-500">{error}</p>
          <button onClick={fetchPost} className="px-4 py-2 bg-[#017e6e] text-white rounded-lg text-sm">Thử lại</button>
        </main>
        <Footer />
      </div>
    );
  }

  if (!post) return null;

  const readTime = estimateReadTime(post.content);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <main className="flex-1">
        {/* ── Back link ─────────────────────────────────────────────────────── */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <Link
            to="/blogs"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#017e6e] hover:text-[#015f54] transition-colors"
          >
            <ArrowLeftIcon />
            Về trang Blog
          </Link>
        </div>

        {/* ── Hero image ────────────────────────────────────────────────────── */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <div className="w-full overflow-hidden rounded-3xl aspect-[21/9]">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* ── Content area ──────────────────────────────────────────────────── */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 mb-16">
          <div className="flex flex-col lg:flex-row gap-10">

            {/* ── Left: Author card (sticky sidebar) ──────────────────────── */}
            <aside className="lg:w-56 shrink-0">
              <div className="lg:sticky lg:top-24 flex flex-row lg:flex-col items-start gap-4 lg:gap-0">
                {/* Author info */}
                <div className="flex lg:flex-col items-center gap-3 w-full">
                  <img
                    src={post.authorAvatar}
                    alt={post.author}
                    className="w-14 h-14 rounded-full object-cover ring-2 ring-[#017e6e]/30 shrink-0"
                  />
                  <div className="lg:mt-3 lg:text-center">
                    <p className="font-semibold text-gray-800 text-sm leading-snug">{post.author}</p>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed hidden lg:block">{post.authorBio}</p>
                  </div>
                </div>

                {/* Follow + share row */}
                <div className="flex lg:flex-col items-center gap-2 mt-0 lg:mt-4 w-full">
                  <button className="w-full py-2 text-sm font-semibold rounded-lg bg-[#017e6e] text-white hover:bg-[#015f54] transition-colors">
                    Theo dõi
                  </button>
                  <button
                    onClick={handleLike}
                    className="lg:hidden p-2 rounded-lg hover:bg-[#017e6e]/10 text-gray-500 hover:text-[#017e6e] transition-colors"
                    title="Thích"
                  >
                    <HeartIcon filled={liked} />
                  </button>
                </div>

                {/* Divider */}
                <div className="hidden lg:block w-full h-px bg-gray-100 my-4" />

                {/* Engagement stats — vertical on desktop */}
                <div className="hidden lg:flex flex-col gap-3 w-full">
                  <button
                    onClick={handleLike}
                    className="flex items-center gap-2.5 text-sm text-gray-600 hover:text-[#017e6e] transition-colors"
                  >
                    <HeartIcon filled={liked} />
                    <span className="font-medium">{post.viewCount + likeCount}</span>
                  </button>

                  <button className="flex items-center gap-2.5 text-sm text-gray-600 hover:text-[#017e6e] transition-colors">
                    <CommentIcon />
                    <span className="font-medium">0</span>
                  </button>

                  <button
                    onClick={() => setSaved((s) => !s)}
                    className="flex items-center gap-2.5 text-sm text-gray-600 hover:text-[#017e6e] transition-colors"
                  >
                    <BookmarkIcon filled={saved} />
                    <span className="font-medium">{saved ? 'Đã lưu' : 'Lưu'}</span>
                  </button>

                  <button className="flex items-center gap-2.5 text-sm text-gray-600 hover:text-[#017e6e] transition-colors">
                    <ShareIcon />
                    <span className="font-medium">Chia sẻ</span>
                  </button>
                </div>
              </div>
            </aside>

            {/* ── Right: Article ─────────────────────────────────────────── */}
            <article className="flex-1 min-w-0">
              {/* Category + read time */}
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs font-semibold uppercase tracking-widest text-[#017e6e]">
                  {post.category}
                </span>
                <span className="text-gray-300">·</span>
                <span className="text-xs text-gray-400">{readTime} phút đọc</span>
              </div>

              {/* Title */}
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 leading-snug">
                {post.title}
              </h1>

              {/* Author meta line */}
              <div className="flex items-center gap-3 mt-5 pb-6 border-b border-gray-100">
                <img
                  src={post.authorAvatar}
                  alt={post.author}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    Bởi{' '}
                    <span className="text-[#017e6e] font-semibold">{post.author}</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">{post.date}</p>
                </div>
              </div>

              {/* Excerpt */}
              <p className="mt-6 text-lg text-gray-600 leading-relaxed font-medium">
                {post.excerpt}
              </p>

              {/* Article body */}
              <TipTapViewer
                content={post.content}
                className="mt-6 prose-green"
              />

              {/* Mobile engagement bar */}
              <div className="lg:hidden flex items-center gap-5 mt-10 pt-6 border-t border-gray-100">
                <button onClick={handleLike} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#017e6e] transition-colors">
                  <HeartIcon filled={liked} />
                  <span>{post.viewCount + likeCount}</span>
                </button>
                <button className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#017e6e] transition-colors">
                  <CommentIcon />
                  <span>0</span>
                </button>
                <button onClick={() => setSaved((s) => !s)} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#017e6e] transition-colors">
                  <BookmarkIcon filled={saved} />
                  <span>{saved ? 'Đã lưu' : 'Lưu'}</span>
                </button>
                <button className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#017e6e] transition-colors">
                  <ShareIcon />
                </button>
              </div>

              {/* Tags / category pill */}
              <div className="mt-8 flex flex-wrap gap-2">
                {post.category && (
                  <span className="px-3 py-1.5 text-xs font-semibold rounded-full bg-[#017e6e]/10 text-[#017e6e]">
                    #{post.category}
                  </span>
                )}
                {post.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1.5 text-xs font-semibold rounded-full bg-[#017e6e]/10 text-[#017e6e]">
                    #{tag}
                  </span>
                ))}
              </div>
            </article>
          </div>
        </div>

        {/* ── Related posts ─────────────────────────────────────────────────── */}
        {relatedPosts.length > 0 && (
          <section className="bg-gray-50 py-12">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-1 h-6 rounded-full bg-[#017e6e]" />
                <h2 className="text-xl font-bold text-gray-800">Bài viết liên quan</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {relatedPosts.map((rp) => (
                  <Link key={rp.id} to={`/blogs/${rp.slug}`} className="group flex flex-col bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={rp.image}
                        alt={rp.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      <span className="text-xs font-semibold text-[#017e6e] uppercase tracking-wide mb-1">
                        {rp.category}
                      </span>
                      <h3 className="text-sm font-semibold text-gray-800 leading-snug group-hover:text-[#017e6e] transition-colors line-clamp-2">
                        {rp.title}
                      </h3>
                      <p className="mt-1.5 text-xs text-gray-400">{rp.author} · {rp.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default BlogDetailPage;
